import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@talentmesh/ui/lib/utils';

const buttonVariants = cva(
    "group/button cursor-pointer inline-flex shrink-0 items-center justify-center rounded-xl border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-colors duration-200 outline-none select-none focus-visible:ring-1 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive dark:aria-invalid:focus-visible:border-destructive/50 dark:aria-invalid:focus-visible:ring-destructive/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:border-primary-foreground/65 focus-visible:ring-primary-foreground/45 dark:focus-visible:border-primary-foreground/55 dark:focus-visible:ring-primary-foreground/40',
                'default-outline':
                    'border border-primary bg-card text-primary hover:bg-primary/10 hover:text-primary focus-visible:border-primary focus-visible:ring-primary',
                'default-outline-transparent':
                    'border border-transparent bg-card text-primary hover:bg-primary/10 hover:text-primary focus-visible:border-primary focus-visible:ring-primary',
                outline:
                    'border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/55 dark:focus-visible:ring-ring/50',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground focus-visible:border-foreground/40 focus-visible:ring-foreground/25 dark:focus-visible:border-foreground/50 dark:focus-visible:ring-foreground/30',
                ghost: 'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-ring/55 dark:focus-visible:ring-ring/50',
                destructive:
                    'bg-destructive/10 text-destructive hover:bg-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 focus-visible:border-destructive focus-visible:ring-destructive dark:focus-visible:border-destructive/90 dark:focus-visible:ring-destructive/80',
                'destructive-outline':
                    'border border-destructive bg-card text-destructive hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive focus-visible:ring-destructive dark:focus-visible:border-destructive/90 dark:focus-visible:ring-destructive/80',
                link: 'text-primary underline-offset-4 hover:underline focus-visible:border-transparent focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            },
            size: {
                default:
                    'h-10 gap-2 px-3.5 py-2 in-data-[slot=button-group]:rounded-xl has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
                xs: "h-8 gap-1 px-2.5 text-xs in-data-[slot=button-group]:rounded-xl has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
                sm: 'h-9 gap-1.5 px-3 in-data-[slot=button-group]:rounded-xl has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
                lg: 'h-11 gap-2 px-8 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
                icon: 'size-10',
                'icon-2xs':
                    "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
                'icon-xs': "size-8 in-data-[slot=button-group]:rounded-xl [&_svg:not([class*='size-'])]:size-3",
                'icon-sm': 'size-9 in-data-[slot=button-group]:rounded-xl',
                'icon-lg': 'size-11',
                'icon-fit': "size-7 [&_svg:not([class*='size-'])]:size-5",
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

function Button({
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    }) {
    const Comp = asChild ? Slot.Root : 'button';

    return (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}

export type ButtonProps = React.ComponentProps<typeof Button>;

export { Button, buttonVariants };
